import api from "@/api"
import router from "@/router/"
import { i18n } from "@/locales/setup.js"
import get from "lodash.get"
import isEqual from "lodash.isequal"

export default {
	async initialize(
		{ commit, dispatch, rootState },
		{ payloadForPolicy, payloadForBeneficiaries }
	) {
		commit("ADD_LOADING", "creating")

		// -----------------------------------------------
		// 1: Initialize policy
		// -----------------------------------------------
		const initizalizePolicyResult = await api.policies.initialize({
			policy: { ...payloadForPolicy }
		})
		if (initizalizePolicyResult.errors) {
			initizalizePolicyResult.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			commit("REMOVE_LOADING", "creating")
			return Promise.resolve()
		}

		const policyId = initizalizePolicyResult.data.initializePolicy.policyStatus
			? initizalizePolicyResult.data.initializePolicy.policyStatus.id
			: null

		// -----------------------------------------------
		// 2: Add beneficiaries to policy if there are any
		// -----------------------------------------------
		if (payloadForBeneficiaries.length === 0 || !policyId) {
			// When no beneficiaries to add, return
			commit("REMOVE_LOADING", "creating")
			return Promise.resolve(policyId)
		}

		let result = await dispatch("addBeneficiaryEligibilities", {
			policyId,
			payloadForBeneficiaries
		})
		if (!result) return Promise.resolve()

		// When all good
		commit("REMOVE_LOADING", "creating")
		return Promise.resolve(policyId)
	},

	async issue({ commit, dispatch, rootState }, { id }) {
		const res = await api.policies.issue({ policyId: id, callApi: false })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve(false)
		} else return Promise.resolve(true)
	},

	async updatePolicy(
		{ commit, dispatch, state },
		{ policyId, payloadForPolicy, payloadForBeneficiaries }
	) {
		commit("ADD_LOADING", "updating")

		// -----------------------------------------------
		// 1: Update policy
		// -----------------------------------------------
		const updatePolicyResult = await api.policies.update({
			policyId,
			updateInput: payloadForPolicy
		})
		if (updatePolicyResult.errors) {
			updatePolicyResult.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			commit("REMOVE_LOADING", "updating")
			return Promise.resolve()
		}

		// policyId = updatePolicyResult.data.initializePolicy.policyStatus ? updatePolicyResult.data.initializePolicy.policyStatus.id : null

		// -----------------------------------------------
		// 2: Update beneficiaries
		//  - Compare original and edited beneficiaries
		//  - If they are different, add new eligibilities and delete old ones
		// -----------------------------------------------
		if (!policyId || !payloadForBeneficiaries) {
			// When no beneficiaries to add, return
			commit("REMOVE_LOADING", "updating")
			return Promise.resolve(policyId)
		}

		// Compare if objects are different
		// If they are different, delete all eligibilities and add new ones
		const currentBeneficiaries = get(
			state,
			"detail.data.beneficiaryEligibilities"
		)
		const newBeneficiaries = payloadForBeneficiaries.map(item => ({
			notes: item.eligibility,
			entity: get(item, "person[0]"),
			id: item.id
		})) // manipulate data to have the same structure as currentbeneficiaries

		if (isEqual(currentBeneficiaries, newBeneficiaries) === true) {
			commit("REMOVE_LOADING", "updating")
			return Promise.resolve(policyId)
		}

		// Firstly add all eligibilities from the form
		let addResult = await dispatch("addBeneficiaryEligibilities", {
			policyId,
			payloadForBeneficiaries
		})
		if (!addResult) {
			commit("REMOVE_LOADING", "updating")
			return Promise.resolve()
		}

		// Delete old eligibilities
		let deleteResult = await dispatch("deleteBeneficiaryEligibilities", {
			policyId,
			payloadForBeneficiaries: currentBeneficiaries
		})
		if (!deleteResult) {
			commit("REMOVE_LOADING", "updating")
			return Promise.resolve()
		}

		// When all good
		commit("REMOVE_LOADING", "updating")
		return Promise.resolve(policyId)
	},

	async updateIssuedPolicy(
		{ commit, dispatch },
		{ policyId, payloadForPolicy }
	) {
		commit("ADD_LOADING", "updating")
		const updatePolicyResult = await api.policies.update({
			policyId,
			updateInput: payloadForPolicy
		})
		commit("REMOVE_LOADING", "updating")

		if (updatePolicyResult.errors) {
			updatePolicyResult.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve()
		}

		return Promise.resolve(true)
	},

	async updateContractInsuredPerson(
		{ commit, dispatch },
		{ policyId, entityId, payload }
	) {
		commit("ADD_LOADING", "updating")
		const result = await api.policies.updateContractInsuredPerson({
			entityId,
			policyId,
			input: payload
		})
		commit("REMOVE_LOADING", "updating")

		if (result.errors) {
			result.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve()
		}

		return Promise.resolve(true)
	},

	async addBeneficiaryEligibilities(
		{ commit, dispatch, state },
		{ policyId, payloadForBeneficiaries }
	) {
		let batches = []

		for (let i = 0; i < payloadForBeneficiaries.length; i++) {
			if (!get(payloadForBeneficiaries[i], "person[0].id")) continue // If beneficiary doesn't have entityId, skip it

			const beneficiaryEligibilityInput = {
				entityId: payloadForBeneficiaries[i].person[0].id,
				ratio: 0,
				notes: payloadForBeneficiaries[i].eligibility
			}
			batches.push(
				api.policies.addBeneficiaryEligibility({
					policyId,
					beneficiaryEligibilityInput
				})
			)
		}

		// Concurrent calls
		let result = null
		await Promise.all(batches).then(value => (result = value))

		// If at least one fetched batches has an error
		if (result.find(batch => batch.errors)) {
			return Promise.resolve(false)
		}

		return Promise.resolve(true)
	},

	async deleteBeneficiaryEligibilities(
		{ commit, dispatch, state },
		{ policyId, payloadForBeneficiaries }
	) {
		if (!payloadForBeneficiaries) return Promise.resolve(true)
		let batches = []

		for (let i = 0; i < payloadForBeneficiaries.length; i++) {
			const beneficiaryEligibilityId = payloadForBeneficiaries[i].id
			console.log(beneficiaryEligibilityId, payloadForBeneficiaries[i])
			batches.push(
				api.policies.deleteBeneficiaryEligibility({
					policyId,
					beneficiaryEligibilityId
				})
			)
		}

		// Concurrent calls
		let result = null
		await Promise.all(batches).then(value => (result = value))

		// If at least one fetched batches has an error
		if (result.find(batch => batch.errors)) {
			return Promise.resolve(false)
		}

		return Promise.resolve(true)
	},

	async readAll({ commit, dispatch, state }) {
		commit("ADD_LOADING", "listing")

		const { sorting, page, itemsPerPage, search, filter } = state.listing

		// Sort out sorting (haha)
		let sort = sorting.find(item => item.isSorting === true) || null
		sort = sort ? { fieldName: sort.field, type: sort.direction } : null

		// Pagination
		const skip = (page - 1) * itemsPerPage
		const limit = itemsPerPage

		// Build filter aka `where`
		// Either search is active or filter is active, not both
		let where = { status_in: ["UNDEFINED", "ISSUED", "CANCELLED"] }
		if (filter.isFiltering) where = filter.where
		if (!filter.isFiltering && search)
			where = {
				and: [
					{ status_in: ["UNDEFINED", "ISSUED", "CANCELLED"] },
					{ issuerNumber_contains: search }
				]
			}

		// Api call
		const res = await api.policies.readAll({ sort, skip, limit, where })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			commit("SET_LIST", [])
		} else {
			commit("SET_LIST", res.data.policies.list)
			commit("SET_TOTAL_COUNT", res.data.policies.totalCount)
		}

		commit("REMOVE_LOADING", "listing")
	},

	async readOne(
		{ commit, dispatch, rootState },
		{ id, flushPreviousData = false }
	) {
		if (flushPreviousData) commit("SET_DETAIL", {}) // reset
		commit("ADD_LOADING", "detail")

		const { tenantId } = rootState
		const res = await api.policies.readOne({ tenantId, where: { id_in: [id] } })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			commit("SET_DETAIL", {})
			commit("SET_BENEFIT_INFOS", [])
		} else {
			commit("SET_DETAIL", res.data.policies.list[0])
			commit("SET_BENEFIT_INFOS", res.data.benefitInfos)
		}

		commit("REMOVE_LOADING", "detail")
	},

	async removeFile({ commit, dispatch, state }, { fileName }) {
		commit("ADD_LOADING", "deleting")

		const policyId = state.detail.data.id
		const locale = window.localStorage["covergo-locale"] || "en"

		const res = await api.policies.deleteFile({ policyId, fileName })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
		} else {
			// Refetch
			dispatch("readOne", { id: policyId })
		}

		commit("REMOVE_LOADING", "deleting")
	},

	async addNote({ commit, dispatch, state, rootState }, payload) {
		commit("ADD_LOADING", "creating")

		const { tenantId } = rootState
		const policyId = state.detail.data.id
		const locale = window.localStorage["covergo-locale"] || "en"
		const res = await api.policies.addNote({
			policyId,
			noteInput: { ...payload }
		})

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
		} else {
			// Refetch and redirect
			dispatch("readOne", { id: policyId })
			router.back()
		}

		commit("REMOVE_LOADING", "creating")
	},

	async updateNote({ commit, dispatch, state }, payload) {
		commit("ADD_LOADING", "updating")

		const policyId = state.detail.data.id
		const locale = window.localStorage["covergo-locale"] || "en"
		const res = await api.policies.updateNote({
			policyId,
			updateNoteInput: { ...payload }
		})

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
		} else {
			// Refetch and redirect
			dispatch("readOne", { id: policyId })
			router.back()
		}

		commit("REMOVE_LOADING", "updating")
	},

	async removeNote({ commit, dispatch, state }, { id }) {
		commit("ADD_LOADING", "deleting")

		const policyId = state.detail.data.id
		const locale = window.localStorage["covergo-locale"] || "en"

		const res = await api.policies.deleteNote({ policyId, id })

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
		} else {
			// Refetch
			dispatch("readOne", { id: policyId })
		}

		commit("REMOVE_LOADING", "deleting")
	},

	async remove({ commit, dispatch, rootState }, { id }) {
		commit("ADD_LOADING", "deleting")
		const res = await api.policies.remove({ id })
		commit("REMOVE_LOADING", "deleting")

		if (res.errors) {
			res.errors.forEach(err =>
				dispatch(
					"addMessage",
					{ message: { type: "error", text: err } },
					{ root: true }
				)
			)
			return Promise.resolve(false)
		} else return Promise.resolve(true)
	}
}
